import type { EngineEventBus, EnginePlugin, EngineContext } from './event-bus'
import { MacroRecorder } from './macro-recorder'
import { WthitInspector } from '../debug/wthit-inspector'
import { DebugLegendHUD } from '../ui/debug-legend'
import { SchematicCameraController } from '../debug/schematic-view'
import type { DebugMaterialRegistry } from '../debug/debug-materials'
import { copyObjectDataAsJson } from '../debug/wthit-copy'
import { GltfHotReloader } from '../level/gltf-hot-reloader'
import type { SoundEffects } from '../audio/sfx'

export interface DiagnosticsBundleConfig {
  bus: EngineEventBus
  context: EngineContext
  debugRegistry?: DebugMaterialRegistry
  schematicCamera?: SchematicCameraController
  width?: number
  height?: number
  enableInspector?: boolean
}

export class DiagnosticsManager {
  readonly macroRecorder: MacroRecorder
  readonly wthitInspector: WthitInspector | null = null
  readonly debugLegendHUD: DebugLegendHUD | null = null
  private readonly plugins: EnginePlugin[] = []
  private readonly debugRegistry?: DebugMaterialRegistry
  private readonly schematicCamera?: SchematicCameraController
  private readonly context: EngineContext

  constructor(config: DiagnosticsBundleConfig) {
    const { bus, context, debugRegistry, schematicCamera, enableInspector = true } = config
    this.context = context
    this.debugRegistry = debugRegistry
    this.schematicCamera = schematicCamera

    // 1. Macro Recorder Plugin
    this.macroRecorder = new MacroRecorder()
    const macroPlugin: EnginePlugin = {
      id: 'macro-recorder',
      install: (eventBus) => {
        eventBus.on('physicsTick', ({ dt, player, telemetry }) => {
          this.macroRecorder.recordTick(dt, player.position, undefined, telemetry)
        })
      },
    }
    this.plugins.push(macroPlugin)
    macroPlugin.install(bus, context)

    // 2. WTHIT Inspector Plugin
    if (enableInspector && debugRegistry) {
      this.wthitInspector = new WthitInspector(
        context.scene,
        context.camera,
        debugRegistry,
      )
      const inspector = this.wthitInspector
      const inspectorPlugin: EnginePlugin = {
        id: 'wthit-inspector',
        install: (eventBus) => {
          eventBus.on('renderTick', () => {
            inspector.update()
          })
        },
      }
      this.plugins.push(inspectorPlugin)
      inspectorPlugin.install(bus, context)
    }

    // 3. Debug Legend HUD Plugin
    if (debugRegistry) {
      this.debugLegendHUD = new DebugLegendHUD()
      const hud = this.debugLegendHUD
      const hudPlugin: EnginePlugin = {
        id: 'debug-legend-hud',
        install: (eventBus) => {
          eventBus.on('renderTick', ({ player }) => {
            hud.update(
              debugRegistry.mode,
              schematicCamera?.isSchematicActive ?? false,
              player.position.x,
              player.position.y,
              player.position.z,
            )
          })
        },
      }
      this.plugins.push(hudPlugin)
      hudPlugin.install(bus, context)
    }
  }

  handleKeyDown(
    event: KeyboardEvent,
    deps: {
      world?: any
      sfx?: SoundEffects
    },
  ): boolean {
    if (event.key === 'F1' || event.code === 'KeyM') {
      if (this.debugRegistry) {
        this.debugRegistry.cycleMode()
        return true
      }
    } else if (event.key === 'F2' || event.code === 'KeyO') {
      if (this.schematicCamera) {
        this.schematicCamera.toggleSchematic()
        return true
      }
    } else if (event.key === 'F3' || event.code === 'KeyH') {
      if (typeof document !== 'undefined') {
        document.body.classList.toggle('hide-chrome')
        return true
      }
    } else if (event.key === 'F4' || event.code === 'KeyR') {
      event.preventDefault()
      if (event.shiftKey) {
        GltfHotReloader.reloadAllProps(this.context.scene, this.debugRegistry, deps.world)
      } else if (this.wthitInspector) {
        GltfHotReloader.reloadTargetedProp(this.wthitInspector, this.context.scene, this.debugRegistry, deps.world)
      }
      return true
    } else if (event.code === 'KeyC' || event.key === 'c' || event.key === 'C') {
      if (this.wthitInspector) {
        const cam = this.schematicCamera?.getCamera() ?? this.context.camera
        copyObjectDataAsJson(this.wthitInspector, cam, this.context.scene, deps.sfx)
        return true
      }
    } else if (event.code === 'KeyT' || event.key === 't' || event.key === 'T') {
      if (this.wthitInspector) {
        const cam = this.schematicCamera?.getCamera() ?? this.context.camera
        const step = event.shiftKey ? -1 : 1
        const result = this.wthitInspector.cycleTargetSwatch(step, cam)
        if (result) {
          deps.sfx?.playScannerChirp()
          return true
        }
      }
    }
    return false
  }

  copyTargetedData(sfx?: SoundEffects): any {
    if (this.wthitInspector) {
      const cam = this.schematicCamera?.getCamera() ?? this.context.camera
      return copyObjectDataAsJson(this.wthitInspector, cam, this.context.scene, sfx)
    }
    return null
  }

  dispose(): void {
    for (const p of this.plugins) {
      p.uninstall?.()
    }
  }
}
